import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Stack, Typography } from '@mui/material';

import { toPromoBrand } from '~/consts/promoBrand';

import { useGetExtendedVoucherWarnings } from '~/hooks/apis/promo/useGetExtendedVoucherWarnings';
import { useGetPromo } from '~/hooks/apis/promo/useGetPromo';
import { useGetPromoLogs } from '~/hooks/apis/promo/useGetPromoLogs';
import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';

import { getVoucherWarnings } from '~/services/PromoService';

import PromoTesterVoucherWarnings from './PromoTesterVoucherWarnings';

const LOG_PAGE_SIZE = 100;

type Props = { codeName?: string; voucherIds: Array<string>; brand: string; orderId?: string; userId?: string };

export default function PromoTesterWarnings({ codeName, voucherIds, brand, orderId, userId }: Props) {
  const [expanded, setExpanded] = useState(false);
  const promoBrand = toPromoBrand(brand);
  const promo = useGetPromo({ codeName, brand, enabled: expanded });
  const vouchers = useGetVouchersByIds({ voucherIds, brand, enabled: expanded && Boolean(promoBrand) });
  const extended = useGetExtendedVoucherWarnings({
    voucherIds,
    brand: promoBrand,
    enabled: expanded && Boolean(promoBrand),
  });
  const logs = useGetPromoLogs({
    query: { codeName, brand: promoBrand, userId, limit: LOG_PAGE_SIZE },
    enabled: expanded && Boolean(promoBrand) && Boolean(orderId),
  });
  const hasInputs = Boolean(codeName || voucherIds.length);
  return (
    <Accordion expanded={expanded} onChange={(_, next) => setExpanded(next)} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Warnings</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {!hasInputs && <Typography variant="body2">Enter a promo code or voucher IDs to check warnings.</Typography>}
          {(promo.isFetching || vouchers.isFetching || extended.isFetching || logs.isFetching) && (
            <Typography variant="body2">Loading warnings…</Typography>
          )}
          {[promo.error, vouchers.error, extended.error, logs.error].filter(Boolean).map((error, index) => (
            <Alert key={index} severity="error">
              Could not load warnings: {error.message}
            </Alert>
          ))}
          {voucherIds.length > 0 && !promoBrand && (
            <Alert severity="warning">Voucher checks are unavailable for this brand.</Alert>
          )}
          {promo.data &&
            (promo.data.result.promo_warnings ?? []).map((warning, index) => (
              <Alert key={`${warning.code}-${index}`} severity={warning.level}>
                Promo {codeName}: {warning.message || warning.code}
              </Alert>
            ))}
          {promo.data && !promo.data.result.promo_warnings?.length && (
            <Typography variant="body2">No promo configuration warnings.</Typography>
          )}
          {vouchers.isSuccess &&
            voucherIds
              .filter((id) => !vouchers.data.has(id))
              .map((id) => (
                <Alert key={id} severity="warning">
                  Voucher {id} was not found for this brand.
                </Alert>
              ))}
          {Array.from(vouchers.data?.values() ?? []).map((voucher) => (
            <Stack key={voucher.voucherId} spacing={1}>
              {(extended.data?.get(voucher.voucherId)?.voucherWarnings ?? getVoucherWarnings(voucher)).map(
                (warning, index) => (
                  <Alert key={`${warning.code}-${index}`} severity={warning.level}>
                    {voucher.voucherName}: {warning.message}
                  </Alert>
                ),
              )}
              {expanded && (
                <PromoTesterVoucherWarnings voucher={voucher} brand={brand} orderId={orderId} userId={userId} />
              )}
            </Stack>
          ))}
          {logs.data?.result?.logs
            .filter((log) => log.fk_orders === orderId)
            .map((log, index) => {
              const results = log.details?.logResults;
              const difference =
                results && typeof results === 'object' && 'promoLogThresholdDifference' in results
                  ? results.promoLogThresholdDifference
                  : undefined;
              return !log.details || (typeof difference === 'number' && Math.abs(difference) >= 0.5) ? (
                <Alert key={index} severity="warning">
                  Promo log for order {log.fk_orders}:{' '}
                  {!log.details ? 'Missing log details.' : `Client/server discount difference: ${difference}%.`}
                </Alert>
              ) : null;
            })}
          {logs.data && (
            <Typography variant="body2">
              Promo log checks cover the first {LOG_PAGE_SIZE} logs for this code{userId ? ' and order customer' : ''},
              filtered to this order.
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
